import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Link2, X, ChevronDown, ChevronRight, ShieldCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { PartnerLinkGuaranteesSubPanel } from "@/components/PartnerLinkGuaranteesSubPanel";

export const RAMI = [
  { v: "auto", l: "Auto" },
  { v: "casa", l: "Casa" },
  { v: "vita", l: "Vita" },
  { v: "salute", l: "Salute" },
  { v: "infortuni", l: "Infortuni" },
  { v: "viaggio", l: "Viaggio" },
  { v: "responsabilita_civile", l: "RC" },
];

const ramoLabel = (v) => (RAMI.find((r) => r.v === v) || {}).l || v;

const emptyForm = {
  linked_company_id: "",
  branch_rates: RAMI.map((r) => ({ ramo: r.v, percent: "" })),
  extra_fees: [],
  notes: "",
  active: true,
  // 🆕 Provvigioni retrocesse dal broker (override per singola compagnia, eredita dal broker se vuoto)
  broker_commission_share_percent: "",
  broker_commission_share_split: "agency_only",
  broker_extra_commission_percent: "",
};

/**
 * Pannello collegamenti partner ↔ compagnia con provvigioni per ramo e spese extra.
 * Props:
 *  - partnerType: "broker" | "agenzia" | "compagnia"
 *  - partnerId: string
 *  - partnerName: string (per UI)
 *  - canWrite: bool
 *  - mode: "by-partner" (default) → mostra le compagnie collegate
 *          "by-company" → mostra i partner collegati (per la pagina Companies)
 */
export default function PartnerCompanyLinksPanel({ partnerType, partnerId, partnerName, canWrite, mode = "by-partner" }) {
  // 🆕 15/07/2026 — Link espanso per mostrare provvigioni per garanzia
  const [expandedLinkId, setExpandedLinkId] = useState(null);
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [offerItems, setOfferItems] = useState([]); // 🆕 Voci offerta dal menù admin
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = mode === "by-company"
        ? { company_id: partnerId }
        : { partner_type: partnerType, partner_id: partnerId };
      const [l, c, b, oi] = await Promise.all([
        api.get("/partner-company-links", { params }),
        api.get("/companies"),
        api.get("/brokers"),
        api.get("/offer-line-items"),
      ]);
      setLinks(l.data || []);
      setCompanies(c.data || []);
      setBrokers(b.data || []);
      setOfferItems(oi.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [partnerType, partnerId, mode]);

  useEffect(() => { load(); }, [load]);

  const companyName = (id) => (companies.find((c) => c.id === id) || {}).name || "—";
  const partnerLabel = (lk) => {
    if (lk.partner_type === "compagnia") return (companies.find((c) => c.id === lk.partner_id) || {}).name || "—";
    return (brokers.find((b) => b.id === lk.partner_id) || {}).name || "—";
  };

  const openAdd = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      // 🆕 09/07/2026 — In modalità by-company la compagnia collegata è quella corrente (partnerId),
      // così l'utente non vede più l'errore "Seleziona una compagnia" quando aggiunge un broker.
      linked_company_id: mode === "by-company" ? partnerId : "",
      branch_rates: RAMI.map((r) => ({ ramo: r.v, percent: "" })),
      extra_fees: [],
    });
    setOpen(true);
  };

  const openEdit = (lk) => {
    setEditing(lk);
    setForm({
      linked_company_id: lk.linked_company_id,
      branch_rates: RAMI.map((r) => {
        const existing = (lk.branch_rates || []).find((br) => br.ramo === r.v);
        return { ramo: r.v, percent: existing ? String(existing.percent) : "" };
      }),
      extra_fees: (lk.extra_fees || []).map((f) => ({
        label: f.label,
        type: f.type,
        value: String(f.value),
        broker_share_percent: f.broker_share_percent != null ? String(f.broker_share_percent) : "0",
        collab_share_percent: f.collab_share_percent != null ? String(f.collab_share_percent) : "0",
      })),
      notes: lk.notes || "",
      active: lk.active !== false,
      broker_commission_share_percent: lk.broker_commission_share_percent != null ? String(lk.broker_commission_share_percent) : "",
      broker_commission_share_split: lk.broker_commission_share_split || "agency_only",
      broker_extra_commission_percent: lk.broker_extra_commission_percent != null ? String(lk.broker_extra_commission_percent) : "",
      // Per by-company permettiamo di modificare ma non cambiare il target compagnia, solo le rate
      _partner_id_for_company_mode: mode === "by-company" ? lk.partner_id : undefined,
      _partner_type_for_company_mode: mode === "by-company" ? lk.partner_type : undefined,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = {
        partner_type: mode === "by-company" ? (editing?.partner_type || form._partner_type_for_company_mode || "broker") : partnerType,
        partner_id: mode === "by-company" ? (editing?.partner_id || form._partner_id_for_company_mode) : partnerId,
        // 🆕 In by-company la compagnia collegata è sempre quella su cui siamo (partnerId)
        linked_company_id: mode === "by-company" ? partnerId : form.linked_company_id,
        branch_rates: form.branch_rates
          .filter((br) => br.percent !== "" && !isNaN(parseFloat(br.percent)))
          .map((br) => ({ ramo: br.ramo, percent: parseFloat(br.percent) })),
        extra_fees: form.extra_fees
          .filter((f) => (f.label || "").trim() && !isNaN(parseFloat(f.value)))
          .map((f) => ({
            label: f.label.trim(),
            type: f.type,
            value: parseFloat(f.value),
            broker_share_percent: parseFloat(f.broker_share_percent || 0) || 0,
            collab_share_percent: parseFloat(f.collab_share_percent || 0) || 0,
          })),
        notes: form.notes || null,
        active: !!form.active,
        broker_commission_share_percent: form.broker_commission_share_percent === "" ? null : parseFloat(form.broker_commission_share_percent),
        broker_commission_share_split: form.broker_commission_share_split || "agency_only",
        broker_extra_commission_percent: form.broker_extra_commission_percent === "" ? null : parseFloat(form.broker_extra_commission_percent),
      };
      if (!payload.linked_company_id) { toast.error("Seleziona una compagnia"); return; }
      if (mode === "by-company" && !payload.partner_id) { toast.error("Seleziona un partner"); return; }
      if (editing) await api.put(`/partner-company-links/${editing.id}`, payload);
      else await api.post("/partner-company-links", payload);
      toast.success("Collegamento salvato");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (lk) => {
    if (!window.confirm("Eliminare questo collegamento?")) return;
    try { await api.delete(`/partner-company-links/${lk.id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Compagnie/Partner già usate → da escludere dalla select in "aggiungi"
  const usedTargets = links.map((l) => mode === "by-company" ? `${l.partner_type}:${l.partner_id}` : l.linked_company_id);

  return (
    <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-4" data-testid={`partner-links-panel-${partnerId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Link2 className="h-4 w-4 text-blue-600" />
          {mode === "by-company" ? `Partner collegati a ${partnerName}` : `Compagnie collegate a ${partnerName}`}
          <span className="text-xs text-slate-500 font-normal">({links.length})</span>
        </div>
        {canWrite && (
          <Button size="sm" data-testid={`add-link-${partnerId}`} onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 h-8">
            <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-slate-500">Caricamento…</p>
      ) : links.length === 0 ? (
        <p className="text-xs text-slate-500 italic">Nessun collegamento. Aggiungi {mode === "by-company" ? "un partner" : "una compagnia"} per impostare le provvigioni.</p>
      ) : (
        <div className="space-y-2">
          {links.map((lk) => (
            <div key={lk.id} data-testid={`link-row-${lk.id}`} className="bg-white border border-slate-200 rounded-md p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 flex items-center gap-2">
                    {mode === "by-company" ? partnerLabel(lk) : companyName(lk.linked_company_id)}
                    {!lk.active && <span className="text-[10px] uppercase bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Inattivo</span>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(lk.branch_rates || []).map((br, i) => (
                      <span key={i} className="text-[11px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-medium tabular-nums">
                        {ramoLabel(br.ramo)}: {br.percent}%
                      </span>
                    ))}
                    {(lk.branch_rates || []).length === 0 && (
                      <span className="text-[11px] text-slate-400 italic">Nessuna provvigione</span>
                    )}
                  </div>
                  {(lk.extra_fees || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {lk.extra_fees.map((f, i) => (
                        <span key={i} className="text-[11px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          + {f.label}: {f.type === "fisso" ? formatCurrency(f.value) : `${f.value}%`}
                        </span>
                      ))}
                    </div>
                  )}
                  {lk.notes && <p className="text-[11px] text-slate-500 mt-1.5">{lk.notes}</p>}
                </div>
                {canWrite && (
                  <div className="flex gap-1 shrink-0">
                    <button
                      data-testid={`toggle-link-guarantees-${lk.id}`}
                      onClick={() => setExpandedLinkId(expandedLinkId === lk.id ? null : lk.id)}
                      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded transition-colors ${expandedLinkId === lk.id ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"}`}
                      title={expandedLinkId === lk.id ? "Chiudi provvigioni per garanzia" : "Provvigioni per singola garanzia"}
                    >
                      {expandedLinkId === lk.id ? <ChevronDown className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
                      Garanzie
                    </button>
                    <button data-testid={`edit-link-${lk.id}`} onClick={() => openEdit(lk)} className="text-blue-600 hover:text-blue-800 p-1"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`delete-link-${lk.id}`} onClick={() => remove(lk)} className="text-red-600 hover:text-red-800 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </div>
              {expandedLinkId === lk.id && (
                <PartnerLinkGuaranteesSubPanel linkId={lk.id} canWrite={canWrite} />
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Link2 className="h-4 w-4 text-blue-600" />
              <span>{editing ? "Modifica collegamento" : "Nuovo collegamento"}</span>
              {partnerName && mode !== "by-company" && (
                <span className="inline-flex items-center gap-1 text-xs font-normal bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full" data-testid="link-dialog-partner-name">
                  <span className="uppercase tracking-wider text-[10px] font-semibold text-blue-600">{partnerType}</span>
                  <span className="font-semibold">{partnerName}</span>
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {mode === "by-company" ? (
              <div>
                <Label>Partner</Label>
                {editing ? (
                  <Input value={partnerLabel(editing)} disabled />
                ) : (
                  <Select
                    value={form._partner_id_for_company_mode ? `${form._partner_type_for_company_mode}:${form._partner_id_for_company_mode}` : ""}
                    onValueChange={(v) => { const [t, id] = v.split(":"); setForm({ ...form, _partner_type_for_company_mode: t, _partner_id_for_company_mode: id }); }}
                  >
                    <SelectTrigger data-testid="link-partner-select"><SelectValue placeholder="Seleziona partner…" /></SelectTrigger>
                    <SelectContent>
                      {brokers.filter((b) => !usedTargets.includes(`${b.type}:${b.id}`)).map((b) => (
                        <SelectItem key={b.id} value={`${b.type}:${b.id}`}>{b.name} ({b.type})</SelectItem>
                      ))}
                      {companies.filter((c) => c.id !== partnerId && !usedTargets.includes(`compagnia:${c.id}`)).map((c) => (
                        <SelectItem key={c.id} value={`compagnia:${c.id}`}>{c.name} (compagnia)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <Label>Compagnia</Label>
                  {!editing && canWrite && (
                    <button
                      type="button"
                      data-testid="link-new-company-btn"
                      onClick={() => {
                        const returnTo = window.location.pathname + window.location.search;
                        navigate(`/companies?new=1&return_to=${encodeURIComponent(returnTo)}`);
                      }}
                      className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                      title="Aggiungi una nuova compagnia e torna qui"
                    >
                      <Plus className="h-3 w-3" /> Nuova compagnia
                    </button>
                  )}
                </div>
                {editing ? (
                  <Input value={companyName(form.linked_company_id)} disabled />
                ) : (
                  <Select value={form.linked_company_id} onValueChange={(v) => setForm({ ...form, linked_company_id: v })}>
                    <SelectTrigger data-testid="link-company-select"><SelectValue placeholder="Seleziona compagnia…" /></SelectTrigger>
                    <SelectContent>
                      {companies
                        .filter((c) => !(partnerType === "compagnia" && c.id === partnerId))
                        .filter((c) => !usedTargets.includes(c.id))
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div>
              <Label className="mb-1.5 block">Provvigione % per ramo</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {form.branch_rates.map((br, i) => (
                  <div key={br.ramo} className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 w-20 shrink-0">{ramoLabel(br.ramo)}</span>
                    <Input
                      data-testid={`link-rate-${br.ramo}`}
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      placeholder="0"
                      value={br.percent}
                      onChange={(e) => {
                        const next = [...form.branch_rates];
                        next[i] = { ...next[i], percent: e.target.value };
                        setForm({ ...form, branch_rates: next });
                      }}
                      className="h-9"
                    />
                    <span className="text-xs text-slate-400">%</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Lascia vuoto i rami non gestiti.</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Spese / commissioni aggiuntive</Label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="link-manage-offer-items"
                    onClick={() => {
                      const returnTo = window.location.pathname + window.location.search;
                      navigate(`/admin/offer-line-items?return_to=${encodeURIComponent(returnTo)}`);
                    }}
                    className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
                    title="Gestisci le Voci offerta dal menù"
                  >
                    Gestisci voci
                  </button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="link-add-fee"
                    onClick={() => setForm({ ...form, extra_fees: [...form.extra_fees, { label: "", type: "fisso", value: "", broker_share_percent: "0", collab_share_percent: "0" }] })}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi voce
                  </Button>
                </div>
              </div>
              {form.extra_fees.length === 0 ? (
                <p className="text-[11px] text-slate-400 italic">
                  Nessuna voce. Aggiungi diritti, commissioni extra, ecc. — entreranno nel totale. Le opzioni provengono dalla lista <b>Voci offerta</b> nel menù.
                </p>
              ) : (
                <div className="space-y-2">
                  {form.extra_fees.map((f, i) => {
                    // 🆕 Voce selezionata dal dropdown (match per nome esatto)
                    const selectedItem = offerItems.find((oi) => oi.name === f.label);
                    return (
                    <div key={i} className="flex items-center gap-2" data-testid={`link-fee-row-${i}`}>
                      <Select
                        value={selectedItem ? selectedItem.id : "__custom__"}
                        onValueChange={(v) => {
                          const n = [...form.extra_fees];
                          if (v === "__custom__") {
                            n[i] = { ...n[i], label: "" };
                          } else {
                            const it = offerItems.find((oi) => oi.id === v);
                            if (it) {
                              n[i] = {
                                ...n[i],
                                label: it.name,
                                // se il valore è vuoto e la voce ha un default, precompila
                                value: (n[i].value === "" || n[i].value == null) && it.default_premium != null ? String(it.default_premium) : n[i].value,
                              };
                            }
                          }
                          setForm({ ...form, extra_fees: n });
                        }}
                      >
                        <SelectTrigger className="h-9 flex-1" data-testid={`link-fee-select-${i}`}>
                          <SelectValue placeholder="Seleziona voce…" />
                        </SelectTrigger>
                        <SelectContent>
                          {offerItems.map((it) => (
                            <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                          ))}
                          <SelectItem value="__custom__">Altro / personalizzata…</SelectItem>
                        </SelectContent>
                      </Select>
                      {!selectedItem && (
                        <Input
                          placeholder="Descrizione personalizzata"
                          value={f.label}
                          data-testid={`link-fee-label-${i}`}
                          onChange={(e) => { const n = [...form.extra_fees]; n[i] = { ...n[i], label: e.target.value }; setForm({ ...form, extra_fees: n }); }}
                          className="h-9 w-56"
                        />
                      )}
                      <Select value={f.type} onValueChange={(v) => { const n = [...form.extra_fees]; n[i] = { ...n[i], type: v }; setForm({ ...form, extra_fees: n }); }}>
                        <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fisso">€ fisso</SelectItem>
                          <SelectItem value="percentuale">% sul premio</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={f.value}
                        onChange={(e) => { const n = [...form.extra_fees]; n[i] = { ...n[i], value: e.target.value }; setForm({ ...form, extra_fees: n }); }}
                        className="h-9 w-28"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, extra_fees: form.extra_fees.filter((_, idx) => idx !== i) })}
                        className="text-red-500 hover:text-red-700 p-1"
                        aria-label="Rimuovi voce"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Label>Note</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note interne (facoltative)" />
            </div>

            {/* 🆕 Retrocessioni broker → agenzia (override per singola compagnia) */}
            {(partnerType === "broker" || editing?.partner_type === "broker") && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 space-y-3">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800">
                  Retrocessioni broker → agenzia (specifico per questa compagnia)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">% provvigione retrocessa</Label>
                    <Input
                      type="number" step="0.1" min="0" max="100"
                      data-testid="link-broker-retro-percent"
                      value={form.broker_commission_share_percent}
                      onChange={(e) => {
                        const v = e.target.value;
                        // 🆕 Feb 2026 — Se l'utente inserisce una % > 0, imposta la modalità "shared" automaticamente
                        const numeric = parseFloat(v);
                        const patch = { broker_commission_share_percent: v };
                        if (!Number.isNaN(numeric) && numeric > 0 && form.broker_commission_share_split === "agency_only") {
                          patch.broker_commission_share_split = "shared";
                        }
                        setForm({ ...form, ...patch });
                      }}
                      placeholder="Lascia vuoto per usare quella del broker"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Destinazione retrocessione</Label>
                    <select
                      data-testid="link-broker-retro-split"
                      value={form.broker_commission_share_split}
                      onChange={(e) => setForm({ ...form, broker_commission_share_split: e.target.value })}
                      className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                    >
                      <option value="agency_only">🏢 Solo per la mia agenzia</option>
                      <option value="shared">🤝 Ripartita</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">% aggiuntiva su altre garanzie (extra-RCA)</Label>
                    <Input
                      type="number" step="0.1" min="0" max="100"
                      data-testid="link-broker-extra-percent"
                      value={form.broker_extra_commission_percent}
                      onChange={(e) => setForm({ ...form, broker_extra_commission_percent: e.target.value })}
                      placeholder="Es. 10 — Lascia vuoto per usare quella del broker"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-emerald-700">
                  Se i campi sono vuoti, il calcolo provvigioni userà i valori configurati sull&apos;anagrafica del broker. Compilali qui per <strong>sovrascrivere</strong> solo per questa compagnia.
                </p>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Collegamento attivo (in uso per il calcolo automatico delle provvigioni)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-link-btn" onClick={submit} className="bg-blue-600 hover:bg-blue-700">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
